import { useState, useEffect } from 'react';
import { apiService } from '../services/api';

interface UseEventThumbnailResult {
  thumbnailUrl: string | null;
  loading: boolean;
  error: string | null;
}

/**
 * Custom hook to fetch event thumbnails using the new API endpoint
 * @param eventId - The event ID to fetch thumbnail for
 * @param fallbackImage - Fallback image URL if thumbnail fetch fails
 * @returns Object containing thumbnailUrl, loading state, and error
 */
export const useEventThumbnail = (
  eventId: number | undefined | null,
  fallbackImage?: string
): UseEventThumbnailResult => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) {
      setThumbnailUrl(fallbackImage || null);
      return;
    }

    const fetchThumbnail = async () => {
      setLoading(true);
      setError(null);
      
      try {
        console.log(`🖼️ useEventThumbnail: Fetching thumbnail for event ID: ${eventId}`);
        const dataUrl = await apiService.getEventThumbnailAsDataUrl(eventId);
        console.log(`✅ useEventThumbnail: Thumbnail fetched successfully for event ID: ${eventId}`);
        setThumbnailUrl(dataUrl);
      } catch (err) {
        console.error(`❌ useEventThumbnail: Failed to fetch thumbnail for event ID: ${eventId}`, err);
        setError(err instanceof Error ? err.message : 'Failed to fetch thumbnail');
        setThumbnailUrl(fallbackImage || null);
      } finally {
        setLoading(false);
      }
    };

    fetchThumbnail();
  }, [eventId, fallbackImage]);

  return { thumbnailUrl, loading, error };
};

/**
 * Hook to get event image with fallback logic
 * @param event - Event object
 * @param eventId - Event ID for thumbnail API call
 * @returns Object containing imageUrl, loading state, and error
 */
export const useEventImage = (event: any, eventId?: number | null): UseEventThumbnailResult => {
  const fallbackImage = 'https://images.pexels.com/photos/6113659/pexels-photo-6113659.jpeg?auto=compress&cs=tinysrgb&w=400';
  
  // If event has thumbnailBase64, use it directly (legacy support)
  if (event?.thumbnailBase64) {
    return {
      thumbnailUrl: event.thumbnailBase64,
      loading: false,
      error: null
    };
  }
  
  // If event has thumbnail.base64, use it directly (legacy support)
  if (event?.thumbnail?.base64) {
    return {
      thumbnailUrl: event.thumbnail.base64,
      loading: false,
      error: null
    };
  }
  
  // If event has imageUrl or IMAGE_URL, use it directly
  if (event?.imageUrl || event?.IMAGE_URL) {
    return {
      thumbnailUrl: event.imageUrl || event.IMAGE_URL,
      loading: false,
      error: null
    };
  }
  
  // If event has images array, use first image
  if ((event?.IMAGES || event?.images) && (event.IMAGES || event.images).length > 0) {
    return {
      thumbnailUrl: (event.IMAGES || event.images)[0],
      loading: false,
      error: null
    };
  }
  
  // Use thumbnail API as last resort
  const actualEventId = eventId || event?.id || event?.EVENT_ID;
  return useEventThumbnail(actualEventId, fallbackImage);
};
